import { useState, useEffect } from 'react';
import { Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material'
import { useNavigate } from "react-router-dom";
import * as GolferHelper from '../library/GolferHelper';
import Golfer, { Loft, LoftShots } from '../classes/Golfer';
import { ShotSelection } from '../classes/ShotSelection';
import { MoveInArray } from '../library/MoveInArray';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {Person} from '@mui/icons-material';
import RankShotsCanvas from './RankShotsCanvas';
import GolferMenu from './GolferMenu';
import { HtmlTooltip } from './HtmlTooltip';
import * as TutorialHelper from '../library/TutorialHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import { logDev } from '../library/Globals';

export default function RankShots(props: any) {
    const [golfer, setGolfer] = useState(new Golfer());
    const [loftList, setLoftList] = useState([] as LoftShots[]);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);

    const intl = useIntl();
    //convert the shot type enum to international text
    const intlShotName = (name: string) => {
        return intl.formatMessage({ id: "shot.selection." + name.replace(" ", "").toLowerCase() });
    }
    const intlMost = intl.formatMessage({ id: "shot.ranking.more" });
    const intlLeast = intl.formatMessage({ id: "shot.ranking.less"});
    const intlOften = intl.formatMessage({ id: "shot.ranking.frequent" });

    useEffect((): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_shot_ranking'
            }
        });

        // resetTimeout();
        const asyncProcess = async () => {
            setShowTutorial(await TutorialHelper.isActive());
            let newGolfer = await GolferHelper.getCurrentGolfer();
            setGolfer(newGolfer);
            let newLoftList = [] as LoftShots[];
            newGolfer.selectedLofts.forEach(sl => {
                let newShot = new LoftShots(sl.lineNumber, sl.loft, []);
                //only show the shots the user selected from previous page
                newShot.shots = sl.shots.filter(sh => sh.selected);
                if (newShot.shots) {
                    newShot.shots.sort((a, b) => a.priority - b.priority);
                }
                //map out which up/down arrows will be displayed for each button
                setVisibleButtonArrows(newShot.shots);
                //save to collection
                newLoftList.push(newShot);
            })
            setLoftList(newLoftList);
        }
        asyncProcess();

    }, []);

    //map out which up/down arrows will be displayed for each button. 
    //shots collection is updated by reference
    const setVisibleButtonArrows = (shots: ShotSelection[]) => {
        for (let ix = 0; ix < shots.length; ix++) {
            //reset values
            shots[ix].upArrow = true;
            shots[ix].downArrow = true;

            //top row has no arrow
            if (ix == 0) {
                shots[ix].upArrow = false;
            }

            //last row has no down arrow
            if (ix == shots.length - 1) {
                shots[ix].downArrow = false;
            }
        }
    }

    let navigate = useNavigate();

    const handleUpArrow = (value: string): void => {
        var [loft, index] = value.split(':');
        moveLoftInList(loft as Loft, Number(index), Number(index) - 1);
    }

    const handleDownArrow = (value: string): void => {
        var [loft, index] = value.split(':');
        moveLoftInList(loft as Loft, Number(index), Number(index) + 1);
    }

    const moveLoftInList = (loft: Loft, from: number, to: number) => {
        let newLoftList = [...loftList];
        let currentLoft = newLoftList.find(l => l.loft == loft) as LoftShots;

        //boundary test
        if (to > -1 && to < currentLoft.shots.length) {
            MoveInArray<ShotSelection>(currentLoft.shots, from, to);
            //re-assign priority based on new array position
            var priority = 0;
            for (let ix = 0; ix < currentLoft.shots.length; ix++) {
                // currentLoft.shots[ix].priority = ix;
                if (currentLoft.shots[ix].selected) {
                    currentLoft.shots[ix].priority = priority++;
                }
                logDev(`${currentLoft.loft}, ${currentLoft.shots[ix].shotType}, ${ix}, ${new Date()}`);
            }
            //re-assign up/down buttons with new positioning
            setVisibleButtonArrows(currentLoft.shots);
            setLoftList(newLoftList);
        }
    }

    const handleSubmit = async () => {
        var selectedLofts = golfer.selectedLofts.map(loft => {
            return {
                loft: loft.loft,
                shots: loft.shots.filter(shot => shot.selected).sort((a, b) => a.priority - b.priority).map(shot => shot.shotType).join (", ")
            }
        })

        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_shot_ranking_start_swinging',
                rank: selectedLofts
            }
        });
        await saveShotsToLocalDB();
        navigate('/apps/wedgefitting/fitting');
    }

    const handleBack = async () => {
        await saveShotsToLocalDB();
        navigate('/apps/wedgefitting/selectshots');
    }

    const saveShotsToLocalDB = async () => {
        //save the ranking order to the selectedLoft collection so we don't lose what the user chose
        golfer.selectedLofts.forEach(sl => {
            //find matching ranked loft if it exists            
            let rankedLofts = loftList.find(ll => ll.loft === sl.loft);
            if (rankedLofts) {
                sl.shots.forEach(sh => {
                    //find matching shot type if exists
                    let rankedShots = rankedLofts!.shots.find(rlsh => rlsh.shotType === sh.shotType);
                    if (rankedShots != undefined) {
                        sh.priority = rankedShots.priority;
                        logDev(`'local db', ${sl.loft}, ${sh.shotType}, ${sh.priority}`);
                    }
                })
            }
        })
        //save it
        await GolferHelper.upsertGolfer(golfer);
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = { ...golfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn  };
        setGolfer(newGolfer);
        await GolferHelper.upsertGolfer(newGolfer);
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId == id;
    }

    const handleTutorialNext = () => {
        if (tutorialId === 1) {
            navigate('/apps/wedgefitting/fitting');
        } else {
            setTutorialId(tutorialId + 1);
        }
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    return (
        <div className="rank-shots-form">
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                <Typography className='name' variant="h6" component="div" sx={{ marginTop: '10px', display: { xs: 'none', md: 'flex' } }}>
                                    <Person sx={{width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em'}}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', marginTop: '7px' }}>
                                    <FormattedMessage id="shot.ranking.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleBack} className='cancel-button'><FormattedMessage id="general.back" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button' sx={{ width: '175px !important' }}><FormattedMessage id="shot.ranking.swing" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth='lg' sx={{ marginTop: '80px' }}>
                <HtmlTooltip
                    title={
                        <>
                            <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.rank.wedges" /></Typography>
                            <div style={{ textAlign: 'right' }}>
                                <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                            </div>
                        </>
                    }
                    open={showPopup(0)}
                    placement={'top'}
                    arrow={false}
                    maxwidth={600}
                >


                    <Grid container className='row-error'>
                        <Grid item xs={12} className='label' sx={{ color: 'red', minHeight: '24px', marginBottom: '10px' }}><span></span>
                        </Grid>
                    </Grid>

                </HtmlTooltip>

                <Grid container className='form-label'>
                    <Grid item xs={12} ><span style={{fontSize: 'x-large'}}><FormattedMessage id="shot.ranking.rank" /></span></Grid>
                </Grid>


                <Grid container className='row' sx={{ marginTop: '20px' }}>
                    <Grid item>
                        <Grid container className='row' sx={{ marginTop: '75px' }}>
                            <Grid item xs={12} sx={{ height:'45px', marginLeft: '0px' }}>
                            <span className='label'>{intlMost}</span><br /><span className='label'>{intlOften}</span>
                            </Grid>
                        </Grid>
                        <Grid container className='form-row-buttons' sx={{ marginTop: '20px', paddingLeft: '10px' }}>
                            <Grid item xs={12}>
                                <RankShotsCanvas id="myCanvas" width="100" height="200" style={{ borderRadius: "5px" }} intl={intl}/>
                            </Grid>
                        </Grid>
                        <Grid container className='row' sx={{ marginTop: '00px' }}>
                            <Grid item xs={12} sx={{ height:'45px', marginLeft: '0px' }}>
                            <span className='label'>{intlLeast}</span><br /><span className='label'>{intlOften}</span>
                            </Grid>
                        </Grid>
                    </Grid>
                    {loftList.map((loft) => {
                        return (
                            <Grid item key={loft.loft}>
                                <Grid container className='row' sx={{ marginTop: '20px' }}>
                                    <Grid item xs={12} sx={{ marginLeft: '-25px' }}>
                                        <span className='form-label rank-shots'>{loft.loft}&#176;</span>
                                    </Grid>
                                </Grid>
                                {loft.shots.map((shot, ix) => {
                                    return (
                                        <div key={ix}>

                                        <Grid container className='rank-shots-buttons' key={ix} sx={{ marginTop: '20px', paddingLeft: '10px' }}>
                                            <Grid item xs={12} sx={{height: '75px'}} className='outline'>
                                                <Grid container sx={{width: '175px', paddingTop: '5px'}}>
                                                    <Grid item xs={9} sx={{ display: 'flex !important', flexDirection: 'column !important', justifyContent: 'center !important', alignItems: 'center !important', fontSize: 'x-large'}}>

                                                    <div style={{width: '125px', fontSize: 'large'}}>{intlShotName(shot.shotType)}</div>
                                                    </Grid>
                                                    <Grid item xs={3} sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                                    {shot.upArrow && (
                                                    <div  style={{height: '25px', width: '40px', marginTop: '0px', marginBottom: '3px', borderStyle: 'solid', borderRadius: '5px', borderColor: 'gray', borderWidth: '.1em'}} onClick={() => (shot.upArrow && handleUpArrow(loft.loft + ':' + ix ))}>
                                                        {shot.upArrow && <ArrowDropUpIcon   sx={{marginTop: '0px', transform: 'scale(1.95)', color: 'gray'}}/>}
                                                    </div>
                                                    )}
                                                    {shot.downArrow && (
                                                    <div  style={{height: '25px', width: '40px', marginTop: '0px', marginBottom: '0px', borderStyle: 'solid', borderRadius: '5px', borderColor: 'gray', borderWidth: '.1em'}} onClick={() => (shot.downArrow && handleDownArrow(loft.loft + ':' + ix ))}>
                                                        {shot.downArrow && <ArrowDropDownIcon sx={{marginTop: '0px', transform: 'scale(1.95)', color: 'gray'}} />}
                                                    </div>
                                                    )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>                                            
                                        </Grid>
                                        </div>
                                    )
                                })
                                }
                            </Grid>
                        )
                    })
                    }
                    <HtmlTooltip
                        title={
                            <>
                                <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.arrows" /></Typography>
                                <div style={{ textAlign: 'right' }}>
                                    <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                    <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                </div>
                            </>
                        }
                        open={showPopup(1)}
                        placement={'left'}
                        arrow={false}
                        maxwidth={400}
                    >
                        <div></div>
                    </HtmlTooltip>
                </Grid>

            </Container>

        </div>
    )
}
